import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, CheckIcon, ChevronDownIcon, CircleXIcon, ClockIcon, FrownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldAlertIcon, ShieldIcon, SparklesIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, faqs, testimonials, tomos } from '../data/content';
import { Button } from './ui/Button';
type Testimonial = {
  initials: string;
  name: string;
  place: string;
  text: string;
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-bold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-accent'}`} data-id="element-16" data-mp="1nmjd2trov4uh">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-extrabold leading-tight text-slate-900 md:text-4xl" data-id="element-17" data-mp="341ulghuxcqf6">
      {children}
    </h2>;
}
function TestimonialCard({
  item
}: {
  item: Testimonial;
}) {
  return <article className="relative flex flex-col rounded-2xl border border-sky-100 bg-white p-6 shadow-sm" data-id="element-18" data-mp="16rvnwrq26clz">
      <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-accent/15" data-id="element-19" data-mp="12nnzkvniiyoa" />
      <div className="mb-4 text-sm text-gold" data-id="element-20" data-mp="xqp346gdmfmx">★★★★★</div>
      <p className="mb-5 flex-1 text-base font-medium italic leading-relaxed text-slate-700" data-id="element-21" data-mp="xs8jdx218qop">
        “{item.text}”
      </p>
      <div className="flex items-center gap-3 border-t border-sky-100 pt-4" data-id="element-22" data-mp="nzkppb6cn2u7">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-accent/20 bg-sky-50 font-display text-sm font-bold text-accent" data-id="element-23" data-mp="j0znx7bg8b3n">
          {item.initials}
        </span>
        <div data-id="element-24" data-mp="a01v7l6gnq0d">
          <p className="text-sm font-bold text-slate-900" data-id="element-25" data-mp="a0uuy2705f90">{item.name}</p>
          <p className="text-xs text-slate-500" data-id="element-26" data-mp="518eljvvnivz">{item.place}</p>
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-14 md:py-16" data-id="element-27" data-mp="3ri7grac95fb1">
      <Eyebrow data-id="element-28" data-mp="d8pkpr6znvt">⭐ Lo que dicen los que ya lo tienen</Eyebrow>
      <SectionTitle data-id="element-29" data-mp="amgumskf2c0">
        Profesionales y personas reales que ya aplican{' '}
        <span className="text-accent" data-id="element-30" data-mp="3hmdtox0wq3f0">el sistema</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-31" data-mp="3mkyv2zrwat51">
        {testimonials.map(item => <TestimonialCard key={item.initials} item={item} data-id="element-32" data-mp="e3ucqqnoijzi" />)}
      </div>
      <div className="mx-auto mt-8 max-w-3xl rounded-3xl border-2 border-sky-200 bg-white p-6 text-center shadow-lg shadow-sky-100" data-id="element-33" data-mp="j3qfkmkn45dh">
        <p className="text-sm font-bold uppercase tracking-wide text-accent" data-id="element-34" data-mp="4dj6gxgivypg">
          ⏰ Oferta de lanzamiento por tiempo limitado
        </p>
        <p className="mt-2 text-xl font-extrabold text-slate-900" data-id="element-35" data-mp="x2ygbjwgbljs">
          Sumate hoy y dejá tu protocolo de rehabilitación{' '}
          <span className="text-accent" data-id="element-36" data-mp="11tyc2ltu3xkp">listo en minutos.</span>
        </p>
        <div className="mt-5" data-id="element-37" data-mp="2mju19gcuk3c1">
          <Button href="#pricing" data-id="element-38" data-mp="2mj25o5j925lr">QUIERO VER MI OFERTA DEL COMBO REHABILITACIÓN</Button>
        </div>
      </div>
    </section>;
}
const pains = ['Repetís siempre los mismos ejercicios porque no tenés un protocolo estructurado por fases.', 'Nadie sabe cuándo está listo para avanzar de fase porque no hay criterios claros.', 'Perdés alumnos porque se re-lesionan, aunque el problema fue la falta de sistema.', 'Invertís 2 o 3 horas buscando ejercicios que podrías tener organizados en 5 minutos.', 'Tenés miedo de empeorar una lesión porque nunca tuviste un marco de trabajo claro.', 'No sabés cómo manejar el retorno al deporte de forma segura y eso te genera inseguridad.'];
const transformations = ['Sin buscar ejercicios en internet', 'Con progresión real fase por fase', 'Criterios objetivos para avanzar', 'Pacientes que vuelven y te recomiendan'];
export function PainAndTransformation() {
  return <>
      <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-16" data-id="element-39" data-mp="3k1kexgqmzw47">
        <div className="mx-auto max-w-4xl" data-id="element-40" data-mp="j634grlj4w6d">
          <Eyebrow danger data-id="element-41" data-mp="n5lrbswwvhbu">😤 La realidad</Eyebrow>
          <SectionTitle data-id="element-42" data-mp="n6dhgo7qomuq">
            ¿Cuántas veces más vas a improvisar con un{' '}
            <span className="text-accent" data-id="element-43" data-mp="s46rxtg2s30t">paciente o alumno lesionado delante tuyo?</span>
          </SectionTitle>
          <p className="mx-auto mt-6 max-w-3xl text-center font-medium text-slate-600" data-id="element-44" data-mp="x5ck9q5tpo9t">
            Amás lo que hacés. Pero nadie te preparó para esto:
          </p>
          <div className="mt-9 grid gap-3 sm:grid-cols-2 md:gap-4" data-id="element-45" data-mp="163y1nhzk6x90">
            {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50/60 p-4" data-id="element-46" data-mp="1awhdoaillkbp">
                <CircleXIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-47" data-mp="1ktvs64kynz1d" />
                <span className="text-sm font-medium text-slate-700 md:text-base" data-id="element-48" data-mp="1krsrmqm6mnie">{pain}</span>
              </div>)}
          </div>
          <div className="mt-10 rounded-2xl border border-accent/20 bg-sky-50 p-6 text-center" data-id="element-49" data-mp="3ab12bmekujy9">
            <p className="text-lg font-bold text-slate-700 md:text-2xl" data-id="element-50" data-mp="35bep3cyr13pg">
              No es falta de ganas.
              <br data-id="element-51" data-mp="30dmlhloqrt5f" />
              <span className="text-xl text-accent md:text-3xl" data-id="element-52" data-mp="30d3eh00mqp66">Es falta de sistema.</span>
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-53" data-mp="3t0z9m0ajw90u">
        <Eyebrow data-id="element-54" data-mp="3t3bo23h3o195">
          <span className="inline-flex items-center gap-2" data-id="element-55" data-mp="3o25ts17sk5xi">
            <SparklesIcon className="h-4 w-4" data-id="element-56" data-mp="3o2nzrpk99yim" /> La transformación
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-57" data-mp="3j9uwl64fqy8n">
          ¿Y si la próxima sesión fuera{' '}
          <span className="text-accent" data-id="element-58" data-mp="3ed4g4hopy0mp">completamente diferente?</span>
        </SectionTitle>
        <div className="mt-8 rounded-3xl border border-accent/20 bg-sky-50/70 p-6 shadow-[0_10px_40px_rgba(2,132,199,.10)] md:p-10" data-id="element-59" data-mp="fmm5zhpw3fpu">
          <p className="text-center text-base font-bold leading-relaxed text-slate-900 md:text-xl" data-id="element-60" data-mp="au2uqreocwo7">
            Abrís el tomo. Identificás la fase del paciente. Aplicás el protocolo. En 5 minutos, la
            sesión está lista.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-61" data-mp="1vdy5q1v3nrk2">
            {transformations.map(item => <div key={item} className="rounded-xl border border-accent/20 bg-white p-3 text-center text-xs font-bold text-accent md:text-sm" data-id="element-62" data-mp="20bzosmsizqvj">
                {item}
              </div>)}
          </div>
          <p className="mt-6 text-center font-medium text-slate-600" data-id="element-63" data-mp="1qc8uom7vndvz">
            Eso es el{' '}
            <strong className="text-slate-900" data-id="element-64" data-mp="1ch5eeb5ht0sd">
              Combo Completo Rehabilitación de Lesiones.
            </strong>
          </p>
        </div>
        <div className="mt-8 text-center" data-id="element-65" data-mp="36tv8asvv29zr">
          <Button href="#pricing" data-id="element-66" data-mp="36r0nh08xqn5t">QUIERO EL SISTEMA COMBO COMPLETO REHABILITACIÓN</Button>
        </div>
      </section>
    </>;
}
const values = ['Más de 250 protocolos técnicos', '5 Tomos completos', 'De principiante a avanzado', 'Sesiones listas para aplicar', 'Creado para profesionales', 'Adaptado para Argentina'];
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-16" data-id="element-67" data-mp="153xpbdq2wejs">
      <Eyebrow data-id="element-68" data-mp="151kzyfhzr1wp">
        <span className="inline-flex items-center gap-2" data-id="element-69" data-mp="151tfyg9eunc4">
          <TrophyIcon className="h-4 w-4" data-id="element-70" data-mp="1itrj8agfxp0d" /> El sistema Rehabilitación 2026
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-71" data-mp="1dvg4blbagoba">
        5 tomos. <span className="text-accent" data-id="element-72" data-mp="1dyc1v8j85az7">Todas las articulaciones cubiertas.</span>
      </SectionTitle>
      <p className="mt-4 text-center font-medium text-slate-600" data-id="element-73" data-mp="1sqw486yjvefl">
        De la fase aguda al retorno al deporte. Todo listo para aplicar hoy.
      </p>

      <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3" data-id="element-74" data-mp="3ulekl8g84pow">
        {values.map(value => <div key={value} className="flex items-center gap-3 rounded-xl border border-sky-100 bg-white p-4 shadow-sm" data-id="element-75" data-mp="3pmttpzmv3id4">
            <span className="rounded-lg bg-sky-100 p-2" data-id="element-76" data-mp="38r69or49kxa2">
              <CheckIcon className="h-4 w-4 text-accent" data-id="element-77" data-mp="38plecza4lab5" />
            </span>
            <span className="text-sm font-bold leading-tight text-slate-800" data-id="element-78" data-mp="3djh79nbj86zg">{value}</span>
          </div>)}
      </div>

      <div className="mt-16" data-id="element-79" data-mp="3ijbyjrz7yyl1">
        <h3 className="text-center text-2xl font-extrabold text-slate-900 md:text-4xl" data-id="element-80" data-mp="2ktqqf6iimqzn">
          El material que necesitás para{' '}
          <span className="text-accent" data-id="element-81" data-mp="2ptmz3j25r6lj">manejar cualquier lesión con confianza.</span>
        </h3>
        <p className="mt-3 text-center text-sm font-medium text-slate-500" data-id="element-82" data-mp="2uqlmlirbgfar">
          Tocá un tomo para ver el contenido
        </p>
        <div className="mt-8 space-y-3" data-id="element-83" data-mp="2yr5kiojprmxy">
          {tomos.map((tomo, index) => <div key={tomo.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors ${open === index ? 'border-accent shadow-lg shadow-sky-100' : 'border-sky-100'}`} data-id="element-84" data-mp="a0w8j7yfsxj4" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-7 md:py-5" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-85" data-mp="1ymwizxrzrj9j">
                <span className="flex items-center gap-3" data-id="element-86" data-mp="1kt5ej3t4apko">
                  <span className="text-2xl" data-id="element-87" data-mp="1fv3o1xtd3nz6">{tomo.icon}</span>
                  <span data-id="element-88" data-mp="1fs7o63xyep83">
                    <b className="block text-slate-900" data-id="element-89" data-mp="1avr3jjpyqszr">
                      Tomo {index + 1} — {tomo.name}
                    </b>
                    <small className="font-bold text-accent" data-id="element-90" data-mp="16uoygm9d7mpv">{tomo.period}</small>
                  </span>
                </span>
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-91" data-mp="11wvv00zg56ta" />
              </button>
              <AnimatePresence initial={false} data-id="element-92" data-mp="2w9lm6ayfk1wz">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} data-id="element-93" data-mp="253l9rwzl3r0i">
                    <ul className="space-y-2 border-t border-sky-100 px-5 py-4 text-sm font-medium text-slate-600 md:px-7" data-id="element-94" data-mp="2e3qe1y9he9ag">
                      {tomo.content.map(line => <li key={line} className="flex gap-2" data-id="element-95" data-mp="1lhou3kl1536o">
                          <span className="text-accent" data-id="element-96" data-mp="1hgd4hf25ohyh">→</span>
                          {line}
                        </li>)}
                    </ul>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
      </div>
    </section>;
}
const beforeAfterRows = [['Improvisabas cada sesión con ejercicios sueltos de internet', 'Abrís el tomo, identificás la fase y ejecutás'], ['Ningún paciente sabía cuándo estaba listo para avanzar de fase', 'Checklist objetivo por fase incluido en cada tomo'], ['Sin criterios objetivos de progresión', 'Progresión real semana a semana con criterios claros'], ['Alumnos que se re-lesionaban y abandonaban', 'Pacientes que mejoran, se quedan y te recomiendan'], ['2 o 3 horas buscando protocolos antes de cada sesión', '5 minutos para tener la sesión completa lista'], ['Miedo constante de empeorar una lesión', 'Confianza real en cada decisión y protocolo de retorno completo']];
export function BeforeAfterAndBonuses() {
  return <>
      <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-16" data-id="element-97" data-mp="3j73ttu2zj0w2">
        <div className="mx-auto max-w-5xl" data-id="element-98" data-mp="3o1zo70vp20cc">
          <Eyebrow data-id="element-99" data-mp="27p1dqzem2zqc">🔄 Antes vs. después</Eyebrow>
          <SectionTitle data-id="element-100" data-mp="27o9gzl9a2vyh">
            No comprás archivos. <span className="text-accent" data-id="element-101" data-mp="2codwb7yg9kn4">Comprás un sistema.</span>
          </SectionTitle>
          <div className="mt-10 grid gap-4 md:grid-cols-2 md:gap-6" data-id="element-102" data-mp="1smb3hxn8em1z">
            <div className="rounded-2xl border border-red-200 bg-red-50/40 p-6" data-id="element-103" data-mp="1sonhjzpvcc9r">
              <h3 className="mb-5 flex items-center gap-2 font-bold text-slate-900" data-id="element-104" data-mp="1olhe0zu54z6z">
                <XIcon className="rounded-lg bg-red-100 p-2 text-red-500" size={40} data-id="element-105" data-mp="1jsy1853a2838" />
                Antes — Sin sistema
              </h3>
              {beforeAfterRows.map(([before]) => <p key={before} className="mb-3 flex gap-2 text-sm font-medium text-slate-600" data-id="element-106" data-mp="19x4e5rrxmw77">
                  <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" data-id="element-107" data-mp="14vx6rttpbz12" />
                  {before}
                </p>)}
            </div>
            <div className="rounded-2xl border-2 border-accent bg-white p-6 shadow-lg shadow-sky-100" data-id="element-108" data-mp="vwa5d80z2t80">
              <h3 className="mb-5 flex items-center gap-2 font-bold text-slate-900" data-id="element-109" data-mp="2v9e7p8wutedy">
                <CheckIcon className="rounded-lg bg-sky-100 p-2 text-accent" size={40} data-id="element-110" data-mp="2v71v7b9v7w8a" />
                Después — Con el Combo Rehabilitación
              </h3>
              {beforeAfterRows.map(([, after]) => <p key={after} className="mb-3 flex gap-2 text-sm font-bold text-slate-800" data-id="element-111" data-mp="32sx7ury8bo9r">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" data-id="element-112" data-mp="37jnf6aq7u173" />
                  {after}
                </p>)}
            </div>
          </div>
          <p className="mt-10 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-113" data-mp="3chx96i07evr4">
            Recuperás <span className="text-accent" data-id="element-114" data-mp="2ewsw9i91r9vq">tiempo, claridad</span> y la seguridad de
            trabajar con un sistema que funciona.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 md:py-16" data-id="element-115" data-mp="2oscrs3jp86jy">
        <Eyebrow data-id="element-116" data-mp="2sv92l82zyydc">
          <span className="inline-flex items-center gap-2" data-id="element-117" data-mp="2svslos4dvssy">
            <GiftIcon className="h-4 w-4" data-id="element-118" data-mp="42x6fo391a1p" /> Bonos exclusivos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-119" data-mp="459nlz16ngdp">
          Además te llevás <span className="text-accent" data-id="element-120" data-mp="83h1mm49i3l7">5 bonos siempre incluidos gratis</span>
        </SectionTitle>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-121" data-mp="6oeclr5nu079">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-sky-100 bg-white p-5 shadow-sm" data-id="element-122" data-mp="1qml2y8xgg3l">
              <div className="flex gap-3" data-id="element-123" data-mp="blmvjtymu89k">
                <span className="text-3xl" data-id="element-124" data-mp="bosl1ab6bc5t">{icon}</span>
                <div data-id="element-125" data-mp="pkwbqtmu92dj">
                  <small className="font-bold uppercase tracking-wide text-accent" data-id="element-126" data-mp="pke9ngdhvtxp">
                    Bono {number}
                  </small>
                  <h3 className="font-bold text-slate-900" data-id="element-127" data-mp="zcjy4g4jft85">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-medium leading-relaxed text-slate-600" data-id="element-128" data-mp="ucnre94ghnow">
                {description}
              </p>
              <div className="flex border-t border-sky-100 pt-4 text-sm font-bold" data-id="element-129" data-mp="18cyjffhi58bn">
                <span className="text-slate-400 line-through" data-id="element-130" data-mp="14cog6al3gw0h">Valor: {value}</span>
                <span className="ml-auto text-accent" data-id="element-131" data-mp="1n0guqwpkl171">GRATIS</span>
              </div>
            </article>)}
        </div>
        <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-accent/20 bg-sky-50 p-6 text-center" data-id="element-132" data-mp="1rxp9soff8p42">
          <p className="font-medium text-slate-600" data-id="element-133" data-mp="1e122pstr8ikf">
            Valor total de bonos: <s className="font-bold text-slate-400" data-id="element-134" data-mp="1iw942tzjxb66">$60.000 ARS</s>
          </p>
          <p className="mt-1 text-lg font-extrabold text-slate-900" data-id="element-135" data-mp="1484gbbln69gj">
            Hoy van <span className="text-accent" data-id="element-136" data-mp="1905x9atr8ocw">incluidos gratis</span> con tu compra.
          </p>
          <div className="mt-6" data-id="element-137" data-mp="v8rgzbr8f19j">
            <Button href="#pricing" fullWidth className="text-xs md:text-sm" data-id="element-138" data-mp="v9j4s0el7up9">
              SÍ, QUIERO MIS 5 BONOS GRATIS ANTES DE QUE SUBA EL PRECIO →
            </Button>
          </div>
        </div>
      </section>
    </>;
}
export function SocialAndFAQ() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-16" data-id="element-139" data-mp="q8d7rqdw9l9x">
      <Eyebrow data-id="element-140" data-mp="2rykcui1lp3qb">
        <span className="inline-flex items-center gap-2" data-id="element-141" data-mp="2rz3wq4ata26z">
          <HelpCircleIcon className="h-4 w-4" data-id="element-142" data-mp="2n7u8971014wo" /> Preguntas frecuentes
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-143" data-mp="2j5qv8dyols9a">
        Lo que más <span className="text-accent" data-id="element-144" data-mp="2j71x288kgpxr">nos preguntan</span>
      </SectionTitle>
      <div className="mt-10 space-y-3" data-id="element-145" data-mp="2e7fl024h5fqj">
        {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white ${open === index ? 'border-accent/50 shadow-lg shadow-sky-100' : 'border-sky-100'}`} data-id="element-146" data-mp="3axomnt0yv1j1" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-3 p-5 text-left font-bold text-slate-900 md:px-6" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-147" data-mp="31vp42waazpdz">
              {question}
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-accent transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-148" data-mp="1lhp4xnja83rs" />
            </button>
            <AnimatePresence initial={false} data-id="element-149" data-mp="1gd4wl3dpkw8a">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-sky-100 px-5 py-4 text-sm font-medium leading-relaxed text-slate-600 md:px-6" data-id="element-150" data-mp="1gb1v9n73vgo9">
                  {answer}
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>
    </section>;
}
const costs = [[ClockIcon, '156 horas por año', '3 horas buscando protocolos × 52 semanas. Tiempo que podrías recuperar desde hoy con los 5 tomos ya listos.'], [AlertCircleIcon, 'Pacientes que no mejoran', 'Sin un protocolo de activación muscular específica y sin criterios claros de progresión de fase, es difícil lograr los resultados que hacen que los alumnos se queden y te recomienden.'], [FrownIcon, 'Alumnos que se re-lesionan', 'Sin un sistema de retorno progresivo, la re-lesión es cuestión de tiempo. Con el sistema sabés exactamente cuándo el tejido está listo para más carga.'], [ShieldAlertIcon, 'Inseguridad profesional', 'Sin un marco de trabajo claro, cada decisión genera dudas. El sistema te da la confianza de saber qué hacer, cuándo hacerlo y cómo progresar.']] as const;
export function GuaranteeAndCost() {
  return <>
      <section className="border-y border-sky-100 bg-sky-50/70 px-4 py-12 md:py-16" data-id="element-151" data-mp="26n5clqeti781">
        <div className="mx-auto max-w-3xl text-center" data-id="element-152" data-mp="22ltib7l00xti">
          <span className="inline-flex rounded-full border-2 border-accent bg-sky-100 p-5 shadow-lg shadow-sky-100" data-id="element-153" data-mp="22l0h0m4t70ir">
            <ShieldIcon className="h-10 w-10 text-accent" data-id="element-154" data-mp="3i0r1dagrm6t1" />
          </span>
          <Eyebrow data-id="element-155" data-mp="3e073u5sa516d">🛡️ Sin riesgo · 100% garantía</Eyebrow>
          <SectionTitle data-id="element-156" data-mp="3lkrdvpyxprib">
            Probalo 7 días. Si no te sirve,{' '}
            <span className="text-accent" data-id="element-157" data-mp="3lmufl79g992v">te devolvemos el 100%.</span>
          </SectionTitle>
          <div className="mt-7 rounded-2xl border border-sky-100 bg-white p-6 text-left font-medium leading-relaxed text-slate-600 md:p-8" data-id="element-158" data-mp="3qhhv4gmh6t5b">
            <p data-id="element-159" data-mp="3gkvds1tcyk81">
              Abrís los tomos.{' '}
              <b className="text-slate-900" data-id="element-160" data-mp="3gnhi7skycjm1">Los aplicás en tus sesiones o en tu propia rehabilitación.</b>
            </p>
            <p className="mt-3" data-id="element-161" data-mp="d4ohwhh6pr9t">
              Y si en 7 días sentís que no era lo que esperabas, escribís a
              compradigitayaa@gmail.com y te devolvemos el dinero.
            </p>
            <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold text-slate-900" data-id="element-162" data-mp="3ujbpy89zp2xk">
              {['Sin vueltas', 'Sin formularios eternos', 'Sin preguntas'].map(text => <span key={text} className="flex items-center gap-1.5" data-id="element-163" data-mp="r2csmwhjfi15">
                  <CheckCircle2Icon className="h-5 w-5 text-accent" data-id="element-164" data-mp="vvxsku2oym2z" />
                  {text}
                </span>)}
            </div>
          </div>
          <p className="mt-6 text-lg font-bold text-slate-900" data-id="element-165" data-mp="2sfv331nwic7g">
            El riesgo no es tuyo. <span className="text-accent" data-id="element-166" data-mp="2dqgwg1bpm8vr">Es nuestro.</span>
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3 text-center" data-id="element-167" data-mp="2docsjjdt99vh">
            <Metric value="<1%" label="Tasa de devoluciones" data-id="element-168" data-mp="2io8vrgeoox9z" />
            <Metric value="+500" label="Profesionales y personas satisfechas" data-id="element-169" data-mp="24q0qml7hupnq" />
            <Metric value="7 días" label="Garantía sin condiciones" data-id="element-170" data-mp="24s52pqfjquz3" />
          </div>
        </div>
      </section>

      <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-16" data-id="element-171" data-mp="1urupw6xfbpkg">
        <div className="mx-auto max-w-4xl" data-id="element-172" data-mp="1utxrzpbup95j">
          <Eyebrow danger data-id="element-173" data-mp="1zrqwmdxi9v3p">⚖️ El costo real</Eyebrow>
          <SectionTitle data-id="element-174" data-mp="1zsirfmja3osx">
            ¿Cuánto te cuesta <span className="text-red-500" data-id="element-175" data-mp="3p6nli5yb4gce">NO tenerlo?</span>
          </SectionTitle>
          <div className="mt-10 grid gap-3 sm:grid-cols-2" data-id="element-176" data-mp="3u58m46hh121d">
            {costs.map(([CostIcon, label, cost]) => <div key={label} className="flex gap-3 rounded-xl border border-sky-100 bg-sky-50/50 p-4 shadow-sm" data-id="element-177" data-mp="1iieu8khvoigb">
                <span className="h-fit rounded-lg border border-red-100 bg-red-50 p-2" data-id="element-178" data-mp="1s76lsrj110qp">
                  <CostIcon className="h-5 w-5 text-red-500" data-id="element-179" data-mp="1s8zo5umlwysw" />
                </span>
                <p data-id="element-180" data-mp="1nedfhz7wtkci">
                  <b className="block text-sm text-slate-900" data-id="element-181" data-mp="217uwpdd4ikzd">{label}</b>
                  <span className="text-sm font-medium text-slate-500" data-id="element-182" data-mp="219xxmsftdyiv">{cost}</span>
                </p>
              </div>)}
          </div>
          <p className="mt-10 rounded-2xl border border-accent/20 bg-white p-6 text-center text-lg font-bold text-slate-700 shadow-sm md:text-2xl" data-id="element-183" data-mp="2b6aozjnw00ag">
            La próxima sesión puede ser igual.
            <br data-id="element-184" data-mp="grhy1xxy7xou" />
            <span className="text-accent" data-id="element-185" data-mp="gt1fhrvcces7">O puede ser la primera con un sistema real.</span>
          </p>
          <div className="mt-8 text-center" data-id="element-186" data-mp="bu888h6ndk0h">
            <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-187" data-mp="11h29um1mmc9f">
              EMPEZAR HOY CON EL SISTEMA COMPLETO →
            </Button>
          </div>
        </div>
      </section>
    </>;
}
function Metric({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="rounded-xl border border-sky-100 bg-white p-3" data-id="element-188" data-mp="1ak3cgcra9l8c">
      <p className="font-display text-xl font-bold text-primary" data-id="element-189" data-mp="1fiemanhkvvqk">{value}</p>
      <p className="mt-1 text-[10px] font-bold leading-tight text-slate-500" data-id="element-190" data-mp="1kevfhw3mx0ai">{label}</p>
    </div>;
}